import { useMemo, useState } from 'react'
import { Alert, Button, Card, Form, Modal, Select, Space, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { Can } from '@/shared/auth/Can'
import { useAuth } from '@/shared/auth/useAuth'
import { getCurrencyCode } from '@/shared/lib/format'
import { money, type Money } from '@/shared/lib/money'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import { useOperationRunner } from '@/shared/ui/useOperationRunner'
import { fetchAgentRoots, fundSubscriber } from '@/features/agent/agentApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { useIdempotencyKey } from '@/shared/lib/useIdempotencyKey'

/**
 * Abonent balansini AGENT depozitidan to'ldirish.
 *
 * <h2>Nega bu alohida komponent kerak bo'ldi</h2>
 * Backend (`POST /api/agents/{id}/fund`) va hatto frontend klienti
 * (`fundSubscriber`) ALLAQACHON bor edi — lekin ularni chaqiradigan
 * birorta tugma yo'q edi. Ya'ni agent panelga kirsa, abonentni ko'rardi,
 * uning balansini to'ldira olmasdi va sababini tushunmasdi.
 *
 * <h2>⚠️ Agent va admin uchun BIR XIL oyna, boshqacha ko'rinish</h2>
 * Pul HAR DOIM biror agentning depozitidan yechiladi — "hech kimning"
 * puli yo'q. Shuning uchun manba tanlanishi kerak:
 *
 * <ul>
 *   <li><b>Agent</b> — `fetchAgentRoots()` unga FAQAT o'z tugunini
 *       qaytaradi (doira `OWN`). Bitta variant qolgani uchun tanlov
 *       ko'rsatilmaydi va u avtomatik olinadi;</li>
 *   <li><b>Admin</b> — bir necha ildiz agent bo'lishi mumkin, shuning
 *       uchun tanlov ko'rsatiladi.</li>
 * </ul>
 *
 * ⚠️ Ro'yxat BO'SH bo'lsa tugma o'chiriladi va sabab yoziladi. Ilgari
 * bunday holatda so'rov `agentId=''` bilan ketib, tushunarsiz 400
 * qaytarardi.
 *
 * <h2>⚠️ "Mablag' yetmadi" — XATO EMAS</h2>
 * Backend bunday holatda HTTP 200 va ichida `FAILED` qaytaradi
 * (`AgentMoneyController.fund` javadoc'i). Shuning uchun natija
 * {@link OperationResultAlert} bilan ko'rsatiladi, `message.error`
 * bilan emas: operator "texnik nosozlik" deb emas, "depozitda pul
 * yetmadi" deb o'qishi kerak.
 */
export function SubscriberFundAction({
  subscriberId,
  disabled,
}: {
  subscriberId: string
  /** `CLOSED` abonentga pul solish ma'nosiz — chaqiruvchi hal qiladi. */
  disabled?: boolean
}) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [form] = Form.useForm<{ agentId: string; amount: Money | null }>()

  const currency = user?.tenant.currency ?? getCurrencyCode()

  /*
    BUG-08 — to'ldirishdan keyin yangi qator qo'lda yangilashsiz chiqmasdi.

    Ilgari bu yerda ikkita ANIQ kalit turardi:
        subscribers.detail(id), subscribers.grants(id)
    Pul harakati esa bulardan tashqarida ham ko'rinadi va aynan o'sha
    joylar eskirib qolardi:

      · `subscribers.overview(id)` — BALANS raqami shu kalitda
        (`SubscriberOverviewCard`). U 15 soniyada o'zi yangilanadi,
        ya'ni operator "pul tushmadimi?" deb bir necha soniya
        ikkilanib turardi;
      · `subscribers.grantOverview(id)` — paketlar kartasining
        "qaysi grant tezlik beryapti" qismi. To'ldirishdan keyin
        avtomatik xarid bo'lsa (`BillingJob`) shu yerda YANGI QATOR
        paydo bo'lishi kerak, lekin kalit bekor qilinmagani uchun
        eski ro'yxat qolardi;
      · `subscribers.list(...)` — ro'yxatdagi balans ustuni.

    Yechim: ANIQ kalitlar o'rniga PREFIKS. `subscribers.all` =
    `['subscribers']` va u yuqoridagilarning HAMMASINI qamrab oladi.
    Kalitni birma-bir sanash — aynan shu nuqsonni qayta keltirib
    chiqaradigan yondashuv: yangi kalit qo'shilganda uni bu yerga
    qo'shish unutiladi va eskirish JIMGINA qaytadi.

    ⚠️ `agents.all` ham bekor qilinadi: pul AGENT depozitidan yechiladi,
    ya'ni agentning balansi va qarzi ham o'zgardi. Usiz agent o'z
    kabinetida eski depozit summasini ko'rib turardi.
  */
  const runner = useOperationRunner([queryKeys.subscribers.all, queryKeys.agents.all])

  const agents = useQuery({
    queryKey: queryKeys.agents.roots,
    queryFn: fetchAgentRoots,
    // ⚠️ Faqat oyna ochilganda: agentlar ro'yxati abonent kartochkasi
    // uchun kerak emas va har ochilishda so'rov yuborish ortiqcha.
    enabled: open,
  })

  /*
    BUG-07 — yopilgan agentlar ro'yxatda turardi.

    `fetchAgentRoots()` HAMMA ildiz tugunni beradi, holat ustuniga
    qaramay. Operator yopilgan agentni tanlab, "Agent yopilgan (…) —
    'abonent balansini to'ldirish' amali bajarilmaydi" degan xato
    olardi: ya'ni ro'yxat mavjud bo'lmagan tanlovni taklif qilardi.

    ⚠️ Filtr `state === 'ACTIVE'` — `!== 'CLOSED'` EMAS. Backend
    (`DepositServiceImpl.fund`) ikki bosqichda tekshiradi va IKKALASI
    ham bu yerda hisobga olinishi kerak:
        CLOSED    -> "Agent yopilgan"
        SUSPENDED -> "Agent faol emas — sotuv qabul qilinmaydi"
                     (`AgentState.canTransact()` faqat ACTIVE da rost)
    Faqat `CLOSED` ni chiqarib tashlash to'xtatilgan agentni ro'yxatda
    qoldirardi va nuqson yarim tuzatilgan bo'lardi.

    ⚠️ Filtr KLIENT tomonida: `/api/agents/roots` holat parametrini
    olmaydi va agent servisiga tegish bu vazifaning doirasidan
    tashqarida. Javob kichkina (odatda 1-3 ta ildiz), ya'ni narxi yo'q.
  */
  const options = useMemo(
    () =>
      (agents.data ?? [])
        .filter((a) => a.state === 'ACTIVE')
        .map((a) => ({ value: a.id, label: `${a.code} — ${a.displayName}` })),
    [agents.data],
  )

  /**
   * Agent BOR, lekin hech biri faol emas — "umuman yo'q" dan boshqa
   * holat va xabari ham boshqa bo'lishi kerak.
   *
   * ⚠️ Aks holda operator "Sizga biriktirilgan agent topilmadi" ni
   * o'qib, administratordan YANGI agent so'rardi — aslida agent bor,
   * u shunchaki yopilgan yoki to'xtatilgan.
   */
  const allAgentsInactive = (agents.data ?? []).length > 0 && options.length === 0

  /** Yagona variant — tanlov ko'rsatilmaydi (agentning o'z kabineti). */
  const soleAgentId = options.length === 1 ? (options[0]?.value ?? null) : null

  // ⚠️ Imzoda SUMMA bor: `FAILED` da oyna ochiq qoladi va operator summani
  // to'g'irlab qayta yuboradi — bu boshqa amal, unga boshqa kalit kerak.
  const watchedAgentId = Form.useWatch('agentId', form)
  const watchedAmount = Form.useWatch('amount', form)
  const idempotencyKey = useIdempotencyKey(
    'fund',
    [soleAgentId ?? watchedAgentId ?? '', subscriberId, watchedAmount?.amount ?? ''].join(':'),
  )

  return (
    <Can permission="payment.create">
      <Card title={t('subscriber.fund.title')}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Typography.Text type="secondary">{t('subscriber.fund.hint')}</Typography.Text>

          {/*
            ⚠️ NATIJA KARTADA FAQAT OYNA YOPIQ BO'LGANDA ko'rinadi.

            Muvaffaqiyatli (`OK`/`PARTIAL`) amaldan keyin oyna yopiladi va
            xabar shu yerda qoladi — operator "bajarildimi?" degan savolga
            javobni kartochkada ko'radi.

            `FAILED` da esa oyna ATAYLAB ochiq qoladi (pastda, `onOk`) va
            xabar OYNANING ICHIGA chiqadi. Ilgari u faqat shu yerda edi:
            `.ant-modal-mask` butun ko'rinishni (o'lchandi: 1700×1200)
            yopib turardi, karta banneri esa uning ORTIDA (y=794) qolardi.
            Ya'ni server aniq sabab aytgan holda — HTTP 200,
            `status=FAILED`, «Mablag' yetarli emas: so'ralgan 9 999 999,99
            AFN, mavjud 0,00 AFN» — operator ekranda HECH NARSA ko'rmasdi.
          */}
          {runner.result && !open ? (
            <OperationResultAlert result={runner.result} closable onClose={runner.clear} />
          ) : null}

          <Button type="primary" disabled={disabled} onClick={() => setOpen(true)}>
            {t('subscriber.fund.action')}
          </Button>
        </Space>
      </Card>

      <Modal
        open={open}
        title={t('subscriber.fund.title')}
        // ⚠️ Bekor qilib chiqishda natija TOZALANADI: yiqilgan urinishning
        // xabari kartada qolib ketsa, u keyingi ochilishda eski va begona
        // bo'lardi. Muvaffaqiyatli amalda oyna `onOk` ichida yopiladi va
        // xabar ataylab SAQLANADI — operator natijani kartada ko'radi.
        onCancel={() => {
          runner.clear()
          setOpen(false)
        }}
        onOk={() =>
          submitOnValid(form, async (values) => {
            const agentId = soleAgentId ?? values.agentId
            const amount = values.amount ?? money(0, currency)
            const outcome = await runner.run(() =>
              fundSubscriber(agentId, subscriberId, amount, idempotencyKey),
            )
            // ⚠️ `FAILED` da oyna OCHIQ qoladi: sabab (mablag' yetmadi)
            // shu yerda ko'rinsin va operator summani o'zgartira olsin.
            if (outcome.status !== 'FAILED') {
              form.resetFields()
              setOpen(false)
            }
          })
        }
        confirmLoading={runner.running}
        okButtonProps={{ disabled: options.length === 0 }}
        okText={t('subscriber.fund.action')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        {agents.isSuccess && options.length === 0 ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              allAgentsInactive
                ? t('subscriber.fund.noActiveAgent')
                : t('subscriber.fund.noAgent')
            }
          />
        ) : null}

        {/*
          ⚠️ XATO SHU YERDA — summani tuzatadigan maydon bilan BIR EKRANDA.
          «Mablag' yetmadi» degan javob aynan boshqa summa kiritish uchun
          kerak; uni oyna ORTIGA yozish uni ko'rinmas qilardi
          (`VoucherVoidModal` naqshi, `QARORLAR.md` §8 — `PARTIAL`/`FAILED`
          yolg'on «bajarildi» bo'lib qolmasin).
        */}
        {runner.result ? (
          <div style={{ marginBottom: 16 }}>
            <OperationResultAlert result={runner.result} closable onClose={runner.clear} />
          </div>
        ) : null}

        <Form form={form} layout="vertical" preserve={false}>
          {soleAgentId === null ? (
            <Form.Item
              name="agentId"
              label={t('subscriber.fund.source')}
              rules={[{ required: true, message: t('subscriber.fund.validationSource') }]}
            >
              <Select options={options} loading={agents.isLoading} showSearch optionFilterProp="label" />
            </Form.Item>
          ) : null}

          <Form.Item
            name="amount"
            label={t('billing.topUp.field')}
            rules={[{ required: true, message: t('billing.topUp.validationAmount') }]}
          >
            <MoneyInput currency={currency} autoFocus />
          </Form.Item>
        </Form>
      </Modal>
    </Can>
  )
}
